package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.Role
import dev.lingvo.shared.ShotStoreImportRequest
import dev.lingvo.shared.StoreAppDto
import dev.lingvo.shared.StoreConnectionDto
import dev.lingvo.shared.StoreConnectionStatus
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.StoreShotsDto

/** A binding to a different connection or platform never enables the first-run shortcut. */
fun storeImportAvailable(role: Role?, platform: StorePlatform, apps: List<StoreAppDto>, connections: List<StoreConnectionDto>): Boolean =
    role in setOf(Role.OWNER, Role.ADMIN) && apps.any { app ->
        app.platform == platform && connections.any { it.id == app.connectionId && it.platform == platform && it.status == StoreConnectionStatus.VERIFIED }
    }

data class StoreImportSelection(val request: ShotStoreImportRequest, val imageCount: Int, val occupied: Boolean) {
    val blocked: Boolean get() = imageCount == 0 || occupied
}

/** The API takes a locale/device cross product; the preview counts that exact scope. */
fun storeImportSelection(
    shots: StoreShotsDto, locales: Set<String>, devices: Set<DeviceClass>, createSlots: Boolean, captures: List<CaptureDto>,
): StoreImportSelection {
    val selected = shots.sets.filter { it.locale in locales && it.deviceClass in devices }
    return StoreImportSelection(ShotStoreImportRequest(locales.sorted(), devices.sortedBy { it.id }, createSlots),
        selected.filter { it.readable }.sumOf { it.images.size }, occupied = captures.any { it.supersededAt == null && it.locale in locales && it.deviceClass in devices })
}

class StoreImportSelectionBlocked : IllegalStateException()

/** Keeps the final occupancy read and write together so authority can be retired across suspension. */
suspend fun submitStoreImport(
    shots: StoreShotsDto, request: ShotStoreImportRequest, authorized: () -> Boolean,
    readCaptures: suspend () -> List<CaptureDto>, send: suspend (ShotStoreImportRequest) -> dev.lingvo.shared.ShotStoreImportDto,
): dev.lingvo.shared.ShotStoreImportDto {
    if (!authorized()) throw kotlin.coroutines.cancellation.CancellationException("Store import authority changed")
    val captures = readCaptures()
    if (!authorized()) throw kotlin.coroutines.cancellation.CancellationException("Store import authority changed")
    val selection = storeImportSelection(shots, request.locales.orEmpty().toSet(), request.devices.orEmpty().toSet(), request.createSlots, captures)
    if (selection.blocked) throw StoreImportSelectionBlocked()
    return send(request)
}
